/**
 * Amplitude Analytics - Main Module
 * 
 * Initialization, user identification, and event tracking.
 * 
 * ARCHITECTURE:
 * - amplitude.js: Init + core functions (this file)
 * - constants.js: Event names and enums
 * - flowId.js: Flow ID management
 * - generationId.js: Generation ID management  
 * - mealSetId.js: Meal Set ID management
 * - events.js: High-level event functions for components
 */

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { getFlowId } from './flowId';
import { getGenerationId } from './generationId';
import { getMealSetId } from './mealSetId';

// =============================================================================
// CONFIGURATION
// =============================================================================

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;

// Debug mode in development
const DEBUG_MODE = process.env.NODE_ENV === 'development';

// =============================================================================
// INITIALIZATION
// =============================================================================

let isInitialized = false;

/**
 * Initialize Amplitude SDK with Session Replay
 * Call this once in App.js on mount
 */
export function initAmplitude() {
  if (isInitialized) {
    if (DEBUG_MODE) console.log('[Amplitude] Already initialized');
    return;
  }

  if (!AMPLITUDE_API_KEY) {
    console.warn('[Amplitude] API key not found. Set REACT_APP_AMPLITUDE_API_KEY in .env');
    return;
  }

  // Initialize Amplitude
  amplitude.init(AMPLITUDE_API_KEY, undefined, {
    // Disable all automatic tracking to avoid circular reference issues
    defaultTracking: false,
    // Server zone - EU for European data residency
    serverZone: 'EU',
    // Privacy & Performance
    flushIntervalMillis: 1000,
    flushQueueSize: 10,
  });

  // Add Session Replay plugin
  const sessionReplay = sessionReplayPlugin({
    // Sample rate: 0.1 = 10% of sessions for production
    sampleRate: 0.1,
    // Privacy settings - mask sensitive data
    privacyConfig: {
      maskTextSelector: ['.sensitive', '[data-private]'],
      unmaskTextSelector: ['.public'],
    },
  });
  amplitude.add(sessionReplay);

  isInitialized = true;
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] Initialized with Session Replay');
  }
}

// =============================================================================
// USER IDENTIFICATION
// =============================================================================

/**
 * Set user ID after authentication
 * @param {string} userId - User ID from backend
 */
export function setUserId(userId) {
  if (!isInitialized) return;
  
  amplitude.setUserId(userId);
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] User ID set:', userId);
  }
}

/**
 * Clear user ID on logout
 */
export function clearUserId() {
  if (!isInitialized) return;
  
  amplitude.reset();
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] User ID cleared');
  }
}

/**
 * Set user properties
 * @param {Object} properties - User properties to set
 */
export function setUserProperties(properties) {
  if (!isInitialized) return;
  
  const identify = new amplitude.Identify();
  
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identify.set(key, value);
    }
  });
  
  amplitude.identify(identify);
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] User properties set:', properties);
  }
}

/**
 * Increment a user property (for counters like total_meal_sets_created)
 * @param {string} property - Property name
 * @param {number} value - Value to increment by (default 1)
 */
export function incrementUserProperty(property, value = 1) {
  if (!isInitialized) return;
  
  const identify = new amplitude.Identify();
  identify.add(property, value);
  amplitude.identify(identify);
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] Incremented ' + property + ' by ' + value);
  }
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Safely sanitize properties to avoid circular references
 * Amplitude SDK can crash when trying to serialize objects with circular refs
 */
function sanitizeProperties(props) {
  if (!props || typeof props !== 'object') {
    return props;
  }
  
  const sanitized = {};
  for (const key in props) {
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      const value = props[key];
      
      // Skip undefined values
      if (value === undefined) {
        continue;
      }
      
      // Only allow primitive types and simple arrays
      if (
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        // Only allow arrays of primitives
        sanitized[key] = value.filter(
          v => v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
        );
      }
      // Skip objects, functions, DOM elements, etc.
    }
  }
  return sanitized;
}

// =============================================================================
// EVENT TRACKING
// =============================================================================

/**
 * Track an event with automatic ID injection
 * 
 * Automatically adds:
 * - flow_id (if exists)
 * - generation_id (if exists)
 * - meal_set_id (if exists)
 * 
 * @param {string} eventName - Event name from EVENTS constant
 * @param {Object} properties - Event properties
 */
export function track(eventName, properties = {}) {
  if (!isInitialized) {
    if (DEBUG_MODE) {
      console.log('[Amplitude] Not initialized, would track:', eventName, properties);
    }
    return;
  }
  
  // Sanitize properties to avoid circular references
  const safeProps = sanitizeProperties(properties);
  
  // Build properties with automatic IDs
  const enrichedProps = {
    ...safeProps,
  };
  
  // Add IDs if they exist
  const flowId = getFlowId();
  const generationId = getGenerationId();
  const mealSetId = getMealSetId();
  
  if (flowId) {
    enrichedProps.flow_id = flowId;
  }
  if (generationId) {
    enrichedProps.generation_id = generationId;
  }
  if (mealSetId) {
    enrichedProps.meal_set_id = mealSetId;
  }
  
  // Track the event
  amplitude.track(eventName, enrichedProps);
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] Track:', eventName, enrichedProps);
  }
}

/**
 * Track event without automatic ID injection
 * Use this for events that don't need flow/generation/meal_set IDs
 * 
 * @param {string} eventName 
 * @param {Object} properties 
 */
export function trackRaw(eventName, properties = {}) {
  if (!isInitialized) {
    if (DEBUG_MODE) {
      console.log('[Amplitude] Not initialized, would track:', eventName, properties);
    }
    return;
  }
  
  // Sanitize properties to avoid circular references
  const safeProps = sanitizeProperties(properties);
  
  amplitude.track(eventName, safeProps);
  
  if (DEBUG_MODE) {
    console.log('[Amplitude] Track (raw):', eventName, safeProps);
  }
}

// =============================================================================
// DEVICE & SESSION INFO
// =============================================================================

/**
 * Get Amplitude device ID (for backend tries limit sync if needed)
 * @returns {string|undefined}
 */
export function getDeviceId() {
  if (!isInitialized) return undefined;
  return amplitude.getDeviceId();
}

/**
 * Get Amplitude session ID
 * @returns {number|undefined}
 */
export function getSessionId() {
  if (!isInitialized) return undefined;
  return amplitude.getSessionId();
}

/**
 * Flush all queued events immediately
 * Call this before page unload if needed
 */
export function flush() {
  if (!isInitialized) return;
  amplitude.flush();
}

// =============================================================================
// EXPORTS
// =============================================================================

export {
  isInitialized,
};
